/* promo-timer.js
 * ──────────────
 * Reusable promo countdown, styled after the Fanny↔CCW "free gift" promo card
 * (free-ccw-offer.js): label + a big red tabular countdown ("Nd Nh Nm Ns") that
 * ticks every second and removes itself at zero.
 *
 * TWO VARIANTS — both lifted verbatim from free-ccw-offer.js, which had one of
 * each (mind which one you pick; Dorian asked for `plain` on the helmet promo,
 * 2026-07-15):
 *   'plain'  (DEFAULT) = `.free-ccw-offer__timer`   — the FANNY-PACK promo card:
 *                        no background, dark bold label, red #dc2626 countdown.
 *   'banner'           = `.free-ccw-suggest__timer` — the cross-sell card: solid
 *                        red bar, white uppercase text, ⏳ icon.
 *
 * Two ways to use it (built for reuse across promos, and control-panel-ready):
 *
 *  1. DECLARATIVE — drop an element and it auto-mounts on load (self-removes at
 *     zero; no callback):
 *       <div data-agb-promo-timer
 *            data-ends-at="2026-07-18T18:00:00+03:00"
 *            data-label="Ends in"
 *            data-variant="plain"></div>
 *
 *  2. PROGRAMMATIC — when you need an onExpire callback (e.g. hide the promo):
 *       window.AGBPromoTimer.mount(hostEl, {
 *         endsAt:   '2026-07-18T18:00:00+03:00',  // ISO-8601 (with offset) or ms epoch
 *         label:    'Ends in',
 *         variant:  'plain',                       // 'plain' | 'banner'
 *         icon:     '⏳',                           // default: none on plain, ⏳ on banner
 *         onExpire: function () { ... }            // fired once at zero
 *       });
 *
 * Colour: override the countdown/bar colour per-instance with the CSS vars
 * `--agb-promo-timer-accent` (plain) / `--agb-promo-timer-bg` (banner), or
 * `data-bg` / `opts.bg`. Isolated vanilla JS, no deps. A future control-panel
 * promo system can render form (1) straight from config.
 */
(function () {
  'use strict';
  if (window.AGBPromoTimer) return;

  var STYLE_ID = 'agb-promo-timer-style';
  function injectCss() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      // shared
      '.agb-promo-timer{display:flex;flex-wrap:wrap;gap:6px}' +
      '.agb-promo-timer__ic{line-height:1;font-size:1.05rem}' +
      '.agb-promo-timer__count{font-variant-numeric:tabular-nums;font-size:1.08rem;font-weight:900}' +
      // 'plain' — the Fanny↔CCW promo-card timer (.free-ccw-offer__timer).
      // Left-aligned like the CCW card (and like the helmet card's heading/tiles);
      // the host can centre it with `justify-content` if a promo ever needs that.
      '.agb-promo-timer--plain{align-items:baseline;' +
        'font-size:.92rem;font-weight:700;color:#111}' +
      '.agb-promo-timer--plain .agb-promo-timer__count{' +
        'color:var(--agb-promo-timer-accent,#dc2626);letter-spacing:.03em}' +
      // 'banner' — the cross-sell card timer (.free-ccw-suggest__timer)
      '.agb-promo-timer--banner{align-items:center;justify-content:center;gap:8px;' +
        'background:var(--agb-promo-timer-bg,#dc2626);color:#fff;border-radius:6px;padding:10px 12px;' +
        'font-size:.85rem;font-weight:800;letter-spacing:.04em;text-transform:uppercase;line-height:1}' +
      '.agb-promo-timer--banner .agb-promo-timer__count{letter-spacing:.05em}';
    document.head.appendChild(s);
  }

  // "Nd Nh Nm Ns" — same shape as the CCW promo (drops the days part at 0d).
  function fmtRemaining(ms) {
    if (ms < 0) ms = 0;
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600);  s -= h * 3600;
    var m = Math.floor(s / 60);    s -= m * 60;
    return (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm ' + s + 's';
  }

  function toMs(v) {
    if (typeof v === 'number') return v;
    var t = Date.parse(v);
    return isNaN(t) ? NaN : t;
  }

  // mount(host, opts) → { stop, el }. Appends a ticking banner into `host`.
  function mount(host, opts) {
    opts = opts || {};
    var noop = { stop: function () {} };
    if (!host) return noop;
    var end = toMs(opts.endsAt);
    if (isNaN(end)) return noop;   // no/invalid deadline → render nothing
    injectCss();

    var variant = (opts.variant === 'banner') ? 'banner' : 'plain';   // plain = the CCW promo-card look
    var el = document.createElement('div');
    el.className = 'agb-promo-timer agb-promo-timer--' + variant;
    el.setAttribute('role', 'timer');
    if (opts.bg) {
      el.style.setProperty(variant === 'banner' ? '--agb-promo-timer-bg' : '--agb-promo-timer-accent', opts.bg);
    }
    // The CCW promo card carries no icon; the cross-sell banner does.
    var icon = (opts.icon === undefined) ? (variant === 'banner' ? '⏳' : '') : opts.icon;
    el.innerHTML =
      (icon ? '<span class="agb-promo-timer__ic" aria-hidden="true"></span>' : '') +
      '<span class="agb-promo-timer__label"></span>' +
      '<span class="agb-promo-timer__count"></span>';
    if (icon) el.querySelector('.agb-promo-timer__ic').textContent = icon;
    el.querySelector('.agb-promo-timer__label').textContent = (opts.label != null ? opts.label : 'Ends in');
    var countEl = el.querySelector('.agb-promo-timer__count');
    host.appendChild(el);

    var timer = null, done = false;
    function stop() { if (timer) { clearTimeout(timer); timer = null; } }
    (function tick() {
      var rem = end - Date.now();
      if (rem <= 0) {
        stop();
        if (el.parentNode) el.parentNode.removeChild(el);
        if (!done) { done = true; if (typeof opts.onExpire === 'function') { try { opts.onExpire(); } catch (e) {} } }
        return;
      }
      countEl.textContent = fmtRemaining(rem);
      timer = setTimeout(tick, 1000);
    })();
    return { stop: stop, el: el };
  }

  // Auto-mount every [data-agb-promo-timer] from its data-* attributes (once).
  function autoMount() {
    var nodes = document.querySelectorAll('[data-agb-promo-timer]:not([data-agb-promo-mounted])');
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-agb-promo-mounted', '1');
      mount(node, {
        endsAt:  node.getAttribute('data-ends-at'),
        label:   node.getAttribute('data-label'),
        variant: node.getAttribute('data-variant') || undefined,
        icon:    node.hasAttribute('data-icon') ? node.getAttribute('data-icon') : undefined,
        bg:      node.getAttribute('data-bg') || undefined
      });
    });
  }

  window.AGBPromoTimer = { mount: mount, fmtRemaining: fmtRemaining, autoMount: autoMount };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', autoMount);
  else autoMount();
})();
